import { Platform } from '@visikon/core-models';
import { SearchCategories } from 'components/mediaLibrary/constants';

export type BlockSearchField =
  | 'all'
  | 'name'
  | 'tag'
  | 'manuscript'
  | 'description'
  | 'platform-ready'
  | 'archived'
  | 'variation'
  | 'speak-variation'
  | 'video-variation';
export type BlockSearchOperator = 'AND' | 'OR';
export type BlockSearchMatchMode = 'contains' | 'exact';

export type BlockSearchNode = BlockSearchGroupNode | BlockSearchTermNode;

export interface BlockSearchGroupNode {
  type: 'group';
  operator: BlockSearchOperator;
  negated: boolean;
  children: BlockSearchNode[];
}

export interface BlockSearchTermNode {
  type: 'term';
  field: BlockSearchField;
  value: string | boolean;
  matchMode: BlockSearchMatchMode;
  negated: boolean;
}

export interface BlockSearchPayload {
  rawQuery: string;
  filter: BlockSearchNode | null;
}

export interface BlockSearchRequest extends BlockSearchPayload {
  page: number;
  limit: number;
}

/**
 * Mirrors the request limits enforced by the backend (POST /v2/blocks/search), so that valid UI input never hits a 400.
 * The raw query cap is deliberately stricter than the backend's (1000 characters).
 */
export const BLOCK_SEARCH_LIMITS = {
  maxPage: 10000,
  maxRawQueryLength: 500,
  maxTermValueLength: 200,
  /** depth(term) = 1, depth(group) = 1 + max(depth(children)) */
  maxDepth: 10,
  /** Total number of nodes (groups + terms) in the filter tree */
  maxNodes: 100,
} as const;

export const BLOCK_SEARCH_FIELDS = [
  'name',
  'tag',
  'manuscript',
  'description',
  'platform-ready',
  'archived',
  'variation',
  'speak-variation',
  'video-variation',
] as const;

type BlockSearchAlias = (typeof BLOCK_SEARCH_FIELDS)[number];

const FIELD_ALIASES: Record<BlockSearchAlias, BlockSearchField> = {
  name: 'name',
  tag: 'tag',
  manuscript: 'manuscript',
  description: 'description',
  'platform-ready': 'platform-ready',
  archived: 'archived',
  variation: 'variation',
  'speak-variation': 'speak-variation',
  'video-variation': 'video-variation',
};

type TermToken = {
  type: 'TERM';
  value: string;
  /** The field as typed, when the field prefix was split off by the tokenizer (quoted scoped values). */
  field?: string;
  quoted: boolean;
  /** For a quoted value: whether its first / last character is an escaped \* (a literal star, not a wildcard). */
  literalStars?: LiteralStars;
  /** Set when a lone '-' (followed by whitespace) came right before this term, so it does not join the previous phrase. */
  separated?: boolean;
};

type LiteralStars = { leading: boolean; trailing: boolean };

type Token = TermToken | { type: 'AND' } | { type: 'OR' } | { type: 'LPAREN' } | { type: 'RPAREN' } | { type: 'NOT' };

class TokenReader {
  constructor(
    private readonly tokens: Token[],
    private index = 0,
  ) {}

  peek(): Token | undefined {
    return this.tokens[this.index];
  }

  consume(): Token {
    const token = this.tokens[this.index];
    if (!token) {
      throw new Error('Unexpected end of query.');
    }

    this.index += 1;
    return token;
  }

  hasMore(): boolean {
    return this.index < this.tokens.length;
  }
}

function isWhitespace(char: string | undefined) {
  return char != null && /\s/.test(char);
}

function isTokenBoundary(char: string | undefined) {
  return char == null || isWhitespace(char) || char === '(' || char === ')';
}

function normalizeField(field: string): BlockSearchField | null {
  const alias = field.toLowerCase();
  return Object.prototype.hasOwnProperty.call(FIELD_ALIASES, alias) ? FIELD_ALIASES[alias as BlockSearchAlias] : null;
}

const UNTERMINATED_QUOTE_MESSAGE = 'Unterminated quote.';

/**
 * Reads a double-quoted string starting at `start` (the index of the opening quote).
 * Supports the escapes \", \\ and \* (a literal star, e.g. for a tag "C*"); any other backslash is kept literally.
 */
function readQuoted(query: string, start: number): { value: string; end: number; literalStars: LiteralStars } {
  let index = start + 1;
  let value = '';
  // Positions in `value` of stars that were escaped, so they are not taken as wildcards
  const escapedStars = new Set<number>();

  while (index < query.length) {
    const char = query[index];
    const next = query[index + 1];

    if (char === '\\' && (next === '"' || next === '\\' || next === '*')) {
      if (next === '*') {
        escapedStars.add(value.length);
      }
      value += next;
      index += 2;
    } else if (char === '"') {
      if (!isTokenBoundary(query[index + 1])) {
        throw new Error('Expected a space after the closing quote.');
      }

      // Wildcards are judged on the trimmed value, so look at its first and last characters after trimming
      const first = value.length - value.trimStart().length;
      const last = value.trimEnd().length - 1;
      return { value, end: index + 1, literalStars: { leading: escapedStars.has(first), trailing: escapedStars.has(last) } };
    } else {
      value += char;
      index += 1;
    }
  }

  throw new Error(UNTERMINATED_QUOTE_MESSAGE);
}

function readWord(query: string, start: number): { token: TermToken; end: number } {
  let index = start;
  let value = '';

  while (index < query.length && !isTokenBoundary(query[index])) {
    const char = query[index];

    // A quoted value is only recognised directly after a known field prefix, e.g. tag:"Heart failure".
    if (char === ':' && query[index + 1] === '"' && !value.includes(':') && normalizeField(value)) {
      const quoted = readQuoted(query, index + 1);
      return { token: { type: 'TERM', field: value, value: quoted.value, quoted: true, literalStars: quoted.literalStars }, end: quoted.end };
    }

    // An unknown filter name before a quote is almost always a typo or an old filter (e.g. title:"..."); the quote would
    // otherwise be literal text, splitting the quoted words into separate terms and operators.
    if (char === ':' && query[index + 1] === '"' && /^[a-z][a-z-]*$/i.test(value)) {
      throw new Error(`Unknown filter "${value}:". Use one of: ${BLOCK_SEARCH_FIELDS.map((field) => `${field}:`).join(', ')}.`);
    }

    value += char;
    index += 1;
  }

  return { token: { type: 'TERM', value, quoted: false }, end: index };
}

function tokenize(query: string): Token[] {
  const tokens: Token[] = [];
  // A lone '-' is ignored, but it still keeps the words on either side from being joined into one phrase.
  let separateNext = false;
  const push = (token: Token) => {
    tokens.push(token.type === 'TERM' && separateNext ? { ...token, separated: true } : token);
    separateNext = false;
  };

  let index = 0;
  while (index < query.length) {
    const current = query[index];

    if (isWhitespace(current)) {
      index += 1;
    } else if (current === '(') {
      push({ type: 'LPAREN' });
      index += 1;
    } else if (current === ')') {
      push({ type: 'RPAREN' });
      index += 1;
    } else if (current === '-') {
      // A run of dashes followed by whitespace ('-', '--') is ignored; otherwise each dash negates what follows ('--b' is b).
      let runEnd = index;
      while (query[runEnd] === '-') {
        runEnd += 1;
      }

      if (isWhitespace(query[runEnd])) {
        separateNext = true;
      } else {
        for (let dash = index; dash < runEnd; dash++) {
          push({ type: 'NOT' });
        }
      }
      index = runEnd;
    } else if (current === '"') {
      const quoted = readQuoted(query, index);
      push({ type: 'TERM', value: quoted.value, quoted: true, literalStars: quoted.literalStars });
      index = quoted.end;
    } else {
      const remainder = query.slice(index);
      const andMatch = remainder.match(/^AND(?=$|\s|\(|\))/i);
      const orMatch = remainder.match(/^OR(?=$|\s|\(|\))/i);

      if (andMatch) {
        push({ type: 'AND' });
        index += andMatch[0].length;
      } else if (orMatch) {
        push({ type: 'OR' });
        index += orMatch[0].length;
      } else {
        const word = readWord(query, index);
        push(word.token);
        index = word.end;
      }
    }
  }

  return tokens;
}

function isUnaryBoundary(token: Token | undefined) {
  return token?.type === 'TERM' || token?.type === 'LPAREN' || token?.type === 'NOT';
}

function parseBooleanValue(rawValue: string, fieldLabel: string): boolean {
  const normalized = rawValue.toLowerCase();

  if (['yes', 'true', '1'].includes(normalized)) {
    return true;
  }

  if (['no', 'false', '0'].includes(normalized)) {
    return false;
  }

  throw new Error(`Expected yes or no for ${fieldLabel} filter.`);
}

function assertValueLength(value: string) {
  if (value.length > BLOCK_SEARCH_LIMITS.maxTermValueLength) {
    throw new Error(`Search term is too long (max ${BLOCK_SEARCH_LIMITS.maxTermValueLength} characters).`);
  }
}

/** The known field of a `field:value` word, e.g. tag:x; null for plain text such as 10:30, https://x.y or Note:. */
function getFieldPrefix(word: string): { rawField: string; field: BlockSearchField } | null {
  const separatorIndex = word.indexOf(':');
  if (separatorIndex <= 0) {
    return null;
  }

  const rawField = word.slice(0, separatorIndex);
  const field = normalizeField(rawField);
  return field ? { rawField, field } : null;
}

/** An unquoted word without a known field prefix. Adjacent plain words are searched as one phrase. */
function isPlainWord(token: Token | undefined): token is TermToken {
  return token?.type === 'TERM' && !token.quoted && token.field === undefined && getFieldPrefix(token.value) === null;
}

/**
 * Wildcards are only supported around the whole value (*value*). They turn a quoted (exact) value into a contains match,
 * e.g. tag:"*heart valve*", and are redundant but accepted on unquoted values, which already match partially.
 */
function parseWildcardValue(
  rawValue: string,
  fieldPrefix: string,
  missingValueMessage: string,
  literalStars?: LiteralStars,
): { value: string; wildcarded: boolean } {
  const leading = rawValue.startsWith('*') && !literalStars?.leading;
  const trailing = rawValue.endsWith('*') && !literalStars?.trailing;

  if (leading && trailing && rawValue.length >= 2) {
    const value = rawValue.slice(1, -1).trim();
    if (value.length === 0) {
      throw new Error(missingValueMessage);
    }

    return { value, wildcarded: true };
  }

  if (leading || trailing) {
    const example = rawValue.replace(/^\*+|\*+$/g, '').trim() || 'text';
    throw new Error(`Wildcards must be placed on both sides of the value, e.g. ${fieldPrefix}*${example}*.`);
  }

  return { value: rawValue, wildcarded: false };
}

/**
 * Builds the all-field contains term for a run of adjacent plain words, joined with single spaces: `this is a test` is one
 * phrase. A word wrapped in wildcards (*heart*) is unwrapped, since the phrase is a contains match anyway.
 */
function buildPhraseTerm(words: string[]): BlockSearchTermNode {
  const missingValueMessage = 'Missing value in search term.';
  const value = words.map((word) => parseWildcardValue(word, '', missingValueMessage).value).join(' ');

  if (value.length > BLOCK_SEARCH_LIMITS.maxTermValueLength) {
    const hint = words.length > 1 ? ' Put AND between words to search for them separately.' : '';
    throw new Error(`Search text is too long (max ${BLOCK_SEARCH_LIMITS.maxTermValueLength} characters).${hint}`);
  }

  return {
    type: 'term',
    field: 'all',
    value,
    matchMode: 'contains',
    negated: false,
  };
}

function buildUnscopedTerm(rawValue: string, literalStars?: LiteralStars): BlockSearchTermNode {
  const missingValueMessage = 'Missing value in search term.';
  const trimmed = rawValue.trim();
  if (trimmed.length === 0) {
    throw new Error(missingValueMessage);
  }

  // Unscoped terms are always contains matches, so *value* is accepted and simply unwrapped.
  const { value } = parseWildcardValue(trimmed, '', missingValueMessage, literalStars);
  assertValueLength(value);

  return {
    type: 'term',
    field: 'all',
    value,
    matchMode: 'contains',
    negated: false,
  };
}

/**
 * Builds a field-scoped term. An unquoted value is a contains match (tag:hospital also matches "hospital-arrival");
 * a quoted value is an exact, case-insensitive match of the whole value (tag:"hospital"), unless it is wrapped in
 * wildcards (tag:"*heart valve*").
 */
function buildScopedTerm(
  rawField: string,
  field: BlockSearchField,
  rawValue: string,
  quoted: boolean,
  literalStars?: LiteralStars,
): BlockSearchTermNode {
  const fieldLabel = rawField.toLowerCase();
  const missingValueMessage = `Missing value for ${fieldLabel} filter.`;
  const trimmed = rawValue.trim();
  if (trimmed.length === 0) {
    throw new Error(missingValueMessage);
  }

  if (field === 'platform-ready' || field === 'archived') {
    return {
      type: 'term',
      field,
      value: parseBooleanValue(trimmed, fieldLabel),
      matchMode: 'exact',
      negated: false,
    };
  }

  const { value, wildcarded } = parseWildcardValue(trimmed, `${fieldLabel}:`, missingValueMessage, literalStars);
  assertValueLength(value);

  return {
    type: 'term',
    field,
    value,
    matchMode: quoted && !wildcarded ? 'exact' : 'contains',
    negated: false,
  };
}

function buildTerm(token: TermToken): BlockSearchTermNode {
  if (token.field !== undefined) {
    return buildScopedTerm(token.field, normalizeField(token.field)!, token.value, token.quoted, token.literalStars);
  }

  if (token.quoted) {
    // A quoted, unscoped phrase is never interpreted as a field or operator.
    return buildUnscopedTerm(token.value, token.literalStars);
  }

  // 'prefix:' is only treated as a field when the prefix is a known field, so 10:30, https://x.y and Note: stay plain text.
  const prefix = getFieldPrefix(token.value);
  if (prefix) {
    return buildScopedTerm(prefix.rawField, prefix.field, token.value.slice(prefix.rawField.length + 1), false);
  }

  return buildPhraseTerm([token.value]);
}

function buildGroup(operator: BlockSearchOperator, children: BlockSearchNode[], negated = false): BlockSearchNode {
  const flattenedChildren = children.flatMap((child) => {
    if (child.type === 'group' && child.operator === operator && !child.negated) {
      return child.children;
    }

    return [child];
  });

  if (flattenedChildren.length === 1 && !negated) {
    return flattenedChildren[0];
  }

  return {
    type: 'group',
    operator,
    negated,
    children: flattenedChildren,
  };
}

function startsOperand(token: Token | undefined) {
  return token != null && token.type !== 'RPAREN' && token.type !== 'AND' && token.type !== 'OR';
}

/** Error for a missing operand; when the next token is AND/OR, hints that it can be quoted to search for the word. */
function missingOperandError(message: string, next: Token | undefined) {
  const hint = next?.type === 'AND' || next?.type === 'OR' ? ` Use quotes ("${next.type.toLowerCase()}") to search for the word itself.` : '';
  return new Error(`${message}${hint}`);
}

/**
 * Parses one operand. Adjacent plain words form a single phrase term unless `allowPhrase` is false, which is used after
 * '-' so that it negates a single word: `heart -lung surgery` is heart AND NOT lung AND surgery.
 */
function parsePrimary(reader: TokenReader, allowPhrase = true): BlockSearchNode {
  const token = reader.peek();

  if (!token) {
    throw new Error('Unexpected end of query.');
  }

  if (token.type === 'NOT') {
    reader.consume();
    if (!startsOperand(reader.peek())) {
      throw missingOperandError("Expected a search term or group after '-'.", reader.peek());
    }

    const expression = parsePrimary(reader, false);
    return { ...expression, negated: !expression.negated };
  }

  if (token.type === 'LPAREN') {
    reader.consume();
    if (reader.peek()?.type === 'RPAREN') {
      throw new Error('Empty parentheses "()" are not allowed.');
    }

    if (!reader.hasMore()) {
      throw new Error('Missing closing parenthesis.');
    }

    const expression = parseExpression(reader);
    if (reader.peek()?.type !== 'RPAREN') {
      throw new Error('Missing closing parenthesis.');
    }

    reader.consume();
    return expression;
  }

  if (token.type === 'TERM') {
    reader.consume();
    if (!isPlainWord(token)) {
      return buildTerm(token);
    }

    const words = [token.value];
    for (let next = reader.peek(); allowPhrase && isPlainWord(next) && !next.separated; next = reader.peek()) {
      reader.consume();
      words.push(next.value);
    }

    return buildPhraseTerm(words);
  }

  if (token.type === 'AND' || token.type === 'OR') {
    throw new Error(
      `${token.type} must be placed between two search terms. Use quotes ("${token.type.toLowerCase()}") to search for the word itself.`,
    );
  }

  throw new Error('Unexpected closing parenthesis.');
}

function parseAnd(reader: TokenReader): BlockSearchNode {
  const children = [parsePrimary(reader)];

  while (reader.hasMore()) {
    const token = reader.peek();
    if (!token || token.type === 'OR' || token.type === 'RPAREN') {
      break;
    }

    if (token.type === 'AND') {
      reader.consume();
      if (!startsOperand(reader.peek())) {
        throw missingOperandError('Expected a search term after AND.', reader.peek());
      }

      children.push(parsePrimary(reader));
    } else if (isUnaryBoundary(token)) {
      children.push(parsePrimary(reader));
    } else {
      break;
    }
  }

  return buildGroup('AND', children);
}

function parseExpression(reader: TokenReader): BlockSearchNode {
  const children = [parseAnd(reader)];

  while (reader.hasMore() && reader.peek()?.type === 'OR') {
    reader.consume();
    if (!startsOperand(reader.peek())) {
      throw missingOperandError('Expected a search term after OR.', reader.peek());
    }

    children.push(parseAnd(reader));
  }

  return buildGroup('OR', children);
}

/** depth(term) = 1, depth(group) = 1 + max(depth(children)), computed on the flattened tree. */
export function getBlockSearchDepth(node: BlockSearchNode): number {
  if (node.type === 'term') {
    return 1;
  }

  return 1 + Math.max(0, ...node.children.map(getBlockSearchDepth));
}

export function countBlockSearchNodes(node: BlockSearchNode): number {
  if (node.type === 'term') {
    return 1;
  }

  return node.children.reduce((count, child) => count + countBlockSearchNodes(child), 1);
}

function assertTreeLimits(filter: BlockSearchNode) {
  if (getBlockSearchDepth(filter) > BLOCK_SEARCH_LIMITS.maxDepth) {
    throw new Error(`Search query is nested too deeply (max ${BLOCK_SEARCH_LIMITS.maxDepth} levels).`);
  }

  if (countBlockSearchNodes(filter) > BLOCK_SEARCH_LIMITS.maxNodes) {
    throw new Error(`Search query is too complex (max ${BLOCK_SEARCH_LIMITS.maxNodes} terms and groups).`);
  }
}

export function parseBlockSearch(query: string): BlockSearchPayload {
  const rawQuery = query.trim();
  if (rawQuery.length === 0) {
    return { rawQuery: '', filter: null };
  }

  if (rawQuery.length > BLOCK_SEARCH_LIMITS.maxRawQueryLength) {
    throw new Error(`Search query is too long (max ${BLOCK_SEARCH_LIMITS.maxRawQueryLength} characters).`);
  }

  const tokens = tokenize(rawQuery);
  if (tokens.length === 0) {
    return { rawQuery: '', filter: null };
  }

  const reader = new TokenReader(tokens);
  const filter = parseExpression(reader);
  if (reader.hasMore()) {
    throw new Error(reader.peek()?.type === 'RPAREN' ? 'Unexpected closing parenthesis.' : 'Unexpected token at the end of query.');
  }

  assertTreeLimits(filter);

  return {
    rawQuery,
    filter,
  };
}

/** Escapes a value for use inside quotes; stars are escaped too, so a value such as "C*" is not read as a wildcard. */
function escapeQuotedValue(value: string): string {
  return value.replace(/[\\"*]/g, '\\$&');
}

/**
 * Builds an exact-match tag query: the quotes make it exact, and escaping lets spaces, parentheses, colons, quotes and
 * stars survive.
 */
export function buildTagSearchQuery(tag: string): string {
  return `tag:"${escapeQuotedValue(tag)}"`;
}

/** Uses the tokenizer itself, so a quote in the middle of a word (which is literal text) does not count as an open quote. */
function isInsideOpenQuote(input: string): boolean {
  try {
    tokenize(input);
    return false;
  } catch (error) {
    return error instanceof Error && error.message === UNTERMINATED_QUOTE_MESSAGE;
  }
}

/** Leading '(' and '-' characters of a token are syntax, not part of the field name being typed. */
const TOKEN_SYNTAX_PREFIX = /^[(-]*/;

export function getFilterNameSuggestions(input: string): string[] {
  if (input.length === 0 || isWhitespace(input[input.length - 1]) || isInsideOpenQuote(input)) {
    return [];
  }

  const lastToken = input.split(/\s/).pop() ?? '';
  const prefix = lastToken.replace(TOKEN_SYNTAX_PREFIX, '').toLowerCase();
  if (prefix.length < 2 || prefix.includes(':') || prefix.includes('"')) {
    return [];
  }

  return BLOCK_SEARCH_FIELDS.filter((field) => field.length > prefix.length && field.startsWith(prefix)).map((field) => `${field}:`);
}

export function applyFilterSuggestion(input: string, suggestion: string): string {
  const suffixMatch = input.match(/(\S+)$/);
  if (!suffixMatch) {
    return input;
  }

  const token = suffixMatch[1];
  const tokenStart = suffixMatch.index ?? input.length - token.length;
  const syntaxPrefix = token.match(TOKEN_SYNTAX_PREFIX)?.[0] ?? '';
  return `${input.slice(0, tokenStart)}${syntaxPrefix}${suggestion}`;
}

/** Common variation values, offered as value suggestions for the variation filters. */
export const COMMON_BLOCK_VARIATIONS: readonly string[] = Platform.PLATFORM_BLOCK_VARRIATIONS;

/**
 * A trailing variation or yes/no filter whose value is being typed, e.g. `video-variation:ma`, `-variation:`,
 * `(variation:"male a` or `archived:y`. The token must start at the beginning of the input or after whitespace or a
 * parenthesis, like the tokenizer's terms. A closed quote (`variation:"male"`) does not match, as the value is complete.
 */
const FILTER_VALUE_TOKEN =
  /(?<![^\s()])[(-]*(speak-variation|video-variation|variation|platform-ready|archived):(?:"((?:[^"\\]|\\.)*)|([^\s"()]*))$/i;

const VARIATION_VALUE_FIELDS: readonly string[] = ['variation', 'speak-variation', 'video-variation'];
const BOOLEAN_VALUE_FIELDS: readonly string[] = ['platform-ready', 'archived'];

interface FilterValueContext {
  /** The filter name, lower case. */
  field: string;
  /** Index in the input where the value (including an opening quote) starts. */
  valueStart: number;
  /** The value typed so far, with quote escapes resolved. */
  partial: string;
}

function getFilterValueContext(input: string, fields: readonly string[]): FilterValueContext | null {
  const match = input.match(FILTER_VALUE_TOKEN);
  if (!match || match.index === undefined || !fields.includes(match[1].toLowerCase())) {
    return null;
  }

  // The filter must not itself be inside an earlier, still open quoted value.
  if (isInsideOpenQuote(input.slice(0, match.index))) {
    return null;
  }

  const [, field, quotedValue, plainValue = ''] = match;
  const quoted = quotedValue !== undefined;
  const rawValue = quoted ? quotedValue : plainValue;

  return {
    field: field.toLowerCase(),
    valueStart: input.length - rawValue.length - (quoted ? 1 : 0),
    partial: quoted ? rawValue.replace(/\\(["\\*])/g, '$1') : rawValue,
  };
}

function getVariationValueContext(input: string) {
  return getFilterValueContext(input, VARIATION_VALUE_FIELDS);
}

/**
 * Suggests common variation values while a variation:, speak-variation: or video-variation: value is typed, quoted or not.
 * Case-insensitive prefix matches come first, then values containing the typed text; an empty value suggests every common
 * variation. A value that equals a common variation is still offered: unquoted it is a contains match (`variation:male`
 * also finds "female"), and picking the suggestion turns it into the exact, quoted value. Wildcards get nothing.
 */
export function getVariationValueSuggestions(input: string): string[] {
  const context = getVariationValueContext(input);
  if (!context || context.partial.includes('*')) {
    return [];
  }

  // Leading spaces inside an open quote are trimmed by the parser; trailing ones may be part of a multi-word value.
  const partial = context.partial.trimStart().toLowerCase();
  const lowerCaseVariations = COMMON_BLOCK_VARIATIONS.map((variation) => variation.toLowerCase());
  const prefixMatches = COMMON_BLOCK_VARIATIONS.filter((_, index) => lowerCaseVariations[index].startsWith(partial));
  const containsMatches = COMMON_BLOCK_VARIATIONS.filter(
    (_, index) => !lowerCaseVariations[index].startsWith(partial) && lowerCaseVariations[index].includes(partial),
  );

  return [...prefixMatches, ...containsMatches];
}

/**
 * Replaces the value of the trailing variation filter with a common variation. The value is always inserted quoted, i.e.
 * as an exact match, since it is a known whole value: `video-variation:ma` becomes `video-variation:"male"`.
 * A leading '-' or '(' is kept.
 */
export function applyVariationValueSuggestion(input: string, value: string): string {
  const context = getVariationValueContext(input);
  if (!context) {
    return input;
  }

  return `${input.slice(0, context.valueStart)}"${escapeQuotedValue(value)}"`;
}

/** The values of the yes/no filters (platform-ready:, archived:) offered as suggestions. */
export const BOOLEAN_FILTER_VALUES: readonly string[] = ['yes', 'no'];

/**
 * Suggests yes and no while a platform-ready: or archived: value is typed, filtered by the typed prefix. A value that is
 * already yes or no gets nothing, as it is complete.
 */
export function getBooleanValueSuggestions(input: string): string[] {
  const context = getFilterValueContext(input, BOOLEAN_VALUE_FIELDS);
  if (!context) {
    return [];
  }

  const partial = context.partial.trim().toLowerCase();
  if (BOOLEAN_FILTER_VALUES.includes(partial)) {
    return [];
  }

  return BOOLEAN_FILTER_VALUES.filter((value) => value.startsWith(partial));
}

/** Replaces the value of the trailing platform-ready: or archived: filter, unquoted: `archived:y` becomes `archived:yes`. */
export function applyBooleanValueSuggestion(input: string, value: string): string {
  const context = getFilterValueContext(input, BOOLEAN_VALUE_FIELDS);
  if (!context) {
    return input;
  }

  return `${input.slice(0, context.valueStart)}${value}`;
}

export interface BlockSearchSuggestion {
  /** A filter name, a common variation value, or yes/no for a yes/no filter */
  kind: 'field' | 'value' | 'boolean';
  text: string;
}

/** Filter name suggestions for the trailing token, or value suggestions while a variation or yes/no filter value is typed. */
export function getBlockSearchSuggestions(input: string): BlockSearchSuggestion[] {
  const fieldSuggestions = getFilterNameSuggestions(input);
  if (fieldSuggestions.length > 0) {
    return fieldSuggestions.map((text) => ({ kind: 'field', text }));
  }

  const variationSuggestions = getVariationValueSuggestions(input);
  if (variationSuggestions.length > 0) {
    return variationSuggestions.map((text) => ({ kind: 'value', text }));
  }

  return getBooleanValueSuggestions(input).map((text) => ({ kind: 'boolean', text }));
}

export function applyBlockSearchSuggestion(input: string, suggestion: BlockSearchSuggestion): string {
  switch (suggestion.kind) {
    case 'field':
      return applyFilterSuggestion(input, suggestion.text);
    case 'boolean':
      return applyBooleanValueSuggestion(input, suggestion.text);
    default:
      return applyVariationValueSuggestion(input, suggestion.text);
  }
}

/** URL param for the search mode ('true' for the new search experience); the blocks overview always sets it, so a shared link opens the same search. */
export const NEW_SEARCH_PARAM = 'newSearch';

/** Whether the new search experience is the default, from VITE_NEW_SEARCH_EXPERIENCE: 'true' turns it on; anything else, including unset, means the classic search. */
export function isNewSearchDefault(envValue: string | undefined = import.meta.env.VITE_NEW_SEARCH_EXPERIENCE): boolean {
  return envValue?.trim().toLowerCase() === 'true';
}

/** The search mode in a URL search string: true for the new search experience, false for the classic search, null when missing or invalid. */
export function getNewSearchParam(search: string): boolean | null {
  const value = new URLSearchParams(search).get(NEW_SEARCH_PARAM);
  if (value === 'true') {
    return true;
  }

  return value === 'false' ? false : null;
}

/** The search a user picked with the search slider: always classic, the environment's default, or always the new search. */
export enum SearchModePreference {
  CLASSIC = 'classic',
  DEFAULT = 'default',
  NEW = 'new',
}

/** A stored preference, falling back to DEFAULT for a missing or unknown value. */
export function parseSearchModePreference(value: string | null | undefined): SearchModePreference {
  return Object.values(SearchModePreference).find((preference) => preference === value) ?? SearchModePreference.DEFAULT;
}

/** Whether a preference opens the new search; DEFAULT follows the environment's default. */
export function preferNewSearch(preference: SearchModePreference, newSearchDefault: boolean): boolean {
  if (preference === SearchModePreference.DEFAULT) {
    return newSearchDefault;
  }

  return preference === SearchModePreference.NEW;
}

/**
 * What the search slider shows: the preference while it agrees with the search shown, otherwise (e.g. a shared link
 * opened the other search) the search actually shown.
 */
export function getShownSearchModePreference(preference: SearchModePreference, newSearchDefault: boolean, isNewSearch: boolean) {
  if (preferNewSearch(preference, newSearchDefault) === isNewSearch) {
    return preference;
  }

  return isNewSearch ? SearchModePreference.NEW : SearchModePreference.CLASSIC;
}

/**
 * The URL search (`?newSearch=...&query=...`) that the Tags Overview links to for a tag: an exact tag term for the new
 * search experience, or the tag in the Tags category for the classic search.
 */
export function buildTagOverviewSearch(tag: string, newSearch: boolean): string {
  return newSearch
    ? `?${NEW_SEARCH_PARAM}=true&query=${encodeURIComponent(buildTagSearchQuery(tag))}`
    : `?${NEW_SEARCH_PARAM}=false&query=${encodeURIComponent(tag)}&category=Tags`;
}

/** The new search's field for each classic search category; All searches every field, like plain words do. */
const CLASSIC_CATEGORY_FIELDS: Record<SearchCategories, BlockSearchField> = {
  [SearchCategories.All]: 'all',
  [SearchCategories.Names]: 'name',
  [SearchCategories.Tags]: 'tag',
  [SearchCategories.Manuscript]: 'manuscript',
  [SearchCategories.Description]: 'description',
};

/** Whether a query reads back as exactly one contains term for the field and value. */
function isContainsTerm(query: string, field: BlockSearchField, value: string): boolean {
  try {
    const { filter } = parseBlockSearch(query);
    return filter?.type === 'term' && filter.field === field && filter.value === value && filter.matchMode === 'contains' && !filter.negated;
  } catch {
    return false;
  }
}

/**
 * A contains term for a classic search term: unquoted when the new search reads it back as that term (`tag:*operation*`,
 * `this is a test`), otherwise quoted with stars, so words such as "and" or a colon are not read as syntax. A star in
 * the term is always quoted (escaped), as `tag:*C**` would be hard to read.
 */
function buildContainsTerm(field: BlockSearchField, value: string): string {
  const prefix = field === 'all' ? '' : `${field}:`;
  const unquoted = field === 'all' ? value : `${prefix}*${value}*`;
  return !value.includes('*') && isContainsTerm(unquoted, field, value) ? unquoted : `${prefix}"*${escapeQuotedValue(value)}*"`;
}

/**
 * The new search query for a classic search, used when switching to the new search experience: the term as a contains
 * match in its category, and "Show archive" (which shows only archived blocks) as `archived:yes`.
 */
export function migrateClassicSearch(searchTerm: string, category: SearchCategories, showArchived: boolean): string {
  const value = searchTerm.trim().replace(/\s+/g, ' ');
  const terms: string[] = [];
  if (value) {
    terms.push(buildContainsTerm(CLASSIC_CATEGORY_FIELDS[category] ?? 'all', value));
  }
  if (showArchived) {
    terms.push('archived:yes');
  }

  return terms.join(' AND ');
}

export function containsArchivedTerm(node: BlockSearchNode): boolean {
  return node.type === 'group' ? node.children.some(containsArchivedTerm) : node.field === 'archived';
}

/** A filter tree with every negation pushed down to the terms (De Morgan), and same-operator groups merged. */
type DescribedNode =
  | { type: 'term'; term: BlockSearchTermNode; negated: boolean }
  | { type: 'group'; operator: BlockSearchOperator; children: DescribedNode[] };

function pushNegationDown(node: BlockSearchNode, negate: boolean): DescribedNode {
  const negated = node.negated !== negate;
  if (node.type === 'term') {
    return { type: 'term', term: node, negated };
  }

  const flippedOperator: BlockSearchOperator = node.operator === 'AND' ? 'OR' : 'AND';
  const operator = negated ? flippedOperator : node.operator;
  const children = node.children
    .map((child) => pushNegationDown(child, negated))
    .flatMap((child) => (child.type === 'group' && child.operator === operator ? child.children : [child]));

  return children.length === 1 ? children[0] : { type: 'group', operator, children };
}

/**
 * How each string field reads in a sentence: `plural` as in "whose tags contain", `singular` as in "with a tag that is
 * exactly". Fields with a single value per block use the same word for both.
 */
const DESCRIBED_FIELDS: Record<Exclude<BlockSearchField, 'all' | 'platform-ready' | 'archived'>, { plural: string; singular: string }> = {
  name: { plural: 'name', singular: 'name' },
  manuscript: { plural: 'manuscript', singular: 'manuscript' },
  description: { plural: 'description', singular: 'description' },
  tag: { plural: 'tags', singular: 'tag' },
  variation: { plural: 'speak or video variations', singular: 'speak or video variation' },
  'speak-variation': { plural: 'speak variations', singular: 'speak variation' },
  'video-variation': { plural: 'video variations', singular: 'video variation' },
};

/** Describes a term as a clause that follows "Blocks", e.g. 'whose tags contain "x"' or 'with a tag that is exactly "x"'. */
function describeTerm(term: BlockSearchTermNode, negated: boolean): string {
  if (term.field === 'platform-ready' || term.field === 'archived') {
    const label = term.field === 'platform-ready' ? 'platform ready' : 'archived';
    return (term.value === true) !== negated ? `that are ${label}` : `that are not ${label}`;
  }

  const value = `"${String(term.value)}"`;
  const contains = term.matchMode === 'contains';

  if (term.field === 'all') {
    const match = contains ? 'contains' : 'is exactly';
    return negated ? `where no field ${match} ${value}` : `where any field ${match} ${value}`;
  }

  const { plural, singular } = DESCRIBED_FIELDS[term.field];
  if (plural === singular) {
    // One value per block: "whose name contains" / "whose name is exactly"
    if (contains) {
      return negated ? `whose ${singular} doesn't contain ${value}` : `whose ${singular} contains ${value}`;
    }

    return negated ? `whose ${singular} is not exactly ${value}` : `whose ${singular} is exactly ${value}`;
  }

  // A list of values (tags, variations): "whose tags contain" / "with a tag that is exactly"
  if (contains) {
    return negated ? `whose ${plural} don't contain ${value}` : `whose ${plural} contain ${value}`;
  }

  return negated ? `with no ${singular} that is exactly ${value}` : `with a ${singular} that is exactly ${value}`;
}

function describeNode(node: DescribedNode, nested: boolean): string {
  if (node.type === 'term') {
    return describeTerm(node.term, node.negated);
  }

  // After merging, a nested group always has the other operator, so it is parenthesized to keep the grouping unambiguous.
  // The outermost group needs no parentheses.
  const text = node.children.map((child) => describeNode(child, true)).join(` ${node.operator} `);
  return nested ? `(${text})` : text;
}

/**
 * Describes a parsed search in plain English, with AND/OR in capitals as in the query, e.g. `-tag:hospital AND name:hospital`
 * becomes 'Blocks whose tags don't contain "hospital" AND whose name contains "hospital". Archived blocks are hidden.'
 * Returns an empty string for an empty search.
 */
export function describeBlockSearch(filter: BlockSearchNode | null): string {
  if (!filter) {
    return '';
  }

  const sentence = `Blocks ${describeNode(pushNegationDown(filter, false), false)}.`;
  // Mirrors the backend, which hides archived blocks unless the filter has an archived: term anywhere
  return containsArchivedTerm(filter) ? sentence : `${sentence} Archived blocks are hidden.`;
}

/** Normalizes a page number (e.g. from the URL) to an integer within 1..maxPage. */
export function clampBlockSearchPage(page: number): number {
  if (!Number.isFinite(page)) {
    return 1;
  }

  return Math.min(BLOCK_SEARCH_LIMITS.maxPage, Math.max(1, Math.floor(page)));
}

export function createBlockSearchRequest(query: string, page: number, limit: number): BlockSearchRequest {
  const payload = parseBlockSearch(query);

  return {
    ...payload,
    page: clampBlockSearchPage(page),
    limit,
  };
}
